import { create } from "zustand";
import * as DescriptionService from "@/components/canvas/detailsSide/descriptions/DescriptionService";
import type { DescriptionData } from "@/components/canvas/detailsSide/descriptions/DescriptionService";
import { createLogger } from "debug/DebugingLogger";

const logger = createLogger("DescriptionStore");

interface DescriptionState {
  descriptions: Record<string, DescriptionData>;
  loading: Record<string, boolean>;
  saving: Record<string, boolean>;
  saved: Record<string, boolean>;
  error: Record<string, boolean>;
}

export type DescriptionEntityType = "model" | "table" | "team";

interface DescriptionActions {
  loadDescription: (
    entityType: DescriptionEntityType,
    entityId: string,
  ) => Promise<void>;
  updateDescription: (
    entityType: DescriptionEntityType,
    entityId: string,
    description: string,
  ) => Promise<void>;
  getDescription: (entityId: string) => string;
  isLoading: (entityId: string) => boolean;
  isSaving: (entityId: string) => boolean;
  isSaved: (entityId: string) => boolean;
  hasError: (entityId: string) => boolean;
  clearEntity: (entityId: string) => void;
  reset: () => void;
}

type DescriptionStore = DescriptionState & DescriptionActions;

const initialState: DescriptionState = {
  descriptions: {},
  loading: {},
  saving: {},
  saved: {},
  error: {},
};

// Track pending timeouts per entity so clearEntity can cancel them
const pendingTimeouts: Record<string, ReturnType<typeof setTimeout>[]> = {};

function trackTimeout(
  entityId: string,
  timeout: ReturnType<typeof setTimeout>,
) {
  (pendingTimeouts[entityId] ??= []).push(timeout);
}

function clearTimeouts(entityId: string) {
  pendingTimeouts[entityId]?.forEach(clearTimeout);
  delete pendingTimeouts[entityId];
}

export const useDescriptionStore = create<DescriptionStore>((set, get) => ({
  ...initialState,

  loadDescription: async (entityType, entityId) => {
    if (get().loading[entityId]) return;
    if (get().descriptions[entityId] !== undefined) return;
    set((s) => ({ loading: { ...s.loading, [entityId]: true } }));
    try {
      let data: DescriptionData;
      if (entityType === "model") {
        data = await DescriptionService.fetchModelDescription(entityId);
      } else if (entityType === "table") {
        data = await DescriptionService.fetchTableDescription(entityId);
      } else {
        data = DescriptionService.fetchTeamDescription(entityId);
      }
      set((s) => ({
        descriptions: { ...s.descriptions, [entityId]: data },
        loading: { ...s.loading, [entityId]: false },
      }));
    } catch (error) {
      logger.error("Failed to load description:", error);
      set((s) => ({ loading: { ...s.loading, [entityId]: false } }));
    }
  },

  updateDescription: async (entityType, entityId, description) => {
    set((s) => ({
      descriptions: {
        ...s.descriptions,
        [entityId]: { ...s.descriptions[entityId], description },
      },
      saving: { ...s.saving, [entityId]: true },
      saved: { ...s.saved, [entityId]: false },
      error: { ...s.error, [entityId]: false },
    }));
    try {
      if (entityType === "model") {
        await DescriptionService.updateModelDescription(entityId, description);
      } else if (entityType === "table") {
        await DescriptionService.updateTableDescription(entityId, description);
      } else {
        await DescriptionService.updateTeamDescription(entityId, description);
      }
      set((s) => ({
        descriptions: {
          ...s.descriptions,
          [entityId]: {
            description,
            updated_by: null,
            updated_at: Date.now(),
          },
        },
        saving: { ...s.saving, [entityId]: false },
        saved: { ...s.saved, [entityId]: true },
      }));
      trackTimeout(
        entityId,
        setTimeout(() => {
          set((s) => ({ saved: { ...s.saved, [entityId]: false } }));
        }, 2000),
      );
    } catch (error) {
      logger.error("Failed to update description:", error);
      set((s) => ({
        saving: { ...s.saving, [entityId]: false },
        error: { ...s.error, [entityId]: true },
      }));
      // Clear stale optimistic update and reload from server
      set((s) => {
        const { [entityId]: _, ...descriptions } = s.descriptions;
        return { descriptions };
      });
      get().loadDescription(entityType, entityId);
      trackTimeout(
        entityId,
        setTimeout(() => {
          set((s) => ({ error: { ...s.error, [entityId]: false } }));
        }, 3000),
      );
    }
  },

  getDescription: (entityId) => {
    return get().descriptions[entityId]?.description ?? "";
  },

  isLoading: (entityId) => {
    return get().loading[entityId] ?? false;
  },

  isSaving: (entityId) => {
    return get().saving[entityId] ?? false;
  },

  isSaved: (entityId) => {
    return get().saved[entityId] ?? false;
  },

  hasError: (entityId) => {
    return get().error[entityId] ?? false;
  },

  clearEntity: (entityId) => {
    clearTimeouts(entityId);
    set((s) => {
      const { [entityId]: _, ...descriptions } = s.descriptions;
      const { [entityId]: _l, ...loading } = s.loading;
      const { [entityId]: _s, ...saving } = s.saving;
      const { [entityId]: _sv, ...saved } = s.saved;
      const { [entityId]: _e, ...error } = s.error;
      return { descriptions, loading, saving, saved, error };
    });
  },

  reset: () => {
    Object.keys(pendingTimeouts).forEach(clearTimeouts);
    set(initialState);
  },
}));
